/**
 * Resource Service Extended
 * Specialized service classes with custom methods for specific resources
 */

import { API_CONFIG } from '../config/api.config';
import { ApiResponse } from './api.service';
import { ResourceService } from './resources.service.base';

// Currency Service with exchange rate helpers
export class CurrencyService extends ResourceService {
  async getActiveCurrencies(): Promise<ApiResponse<any[]>> {
    return this.getFiltered({ isActive: { $eq: true } });
  }

  async getExchangeRate(currencyCode: string): Promise<number | null> {
    const response = await this.search(currencyCode, 'code');
    const currency = response.data?.[0];
    return currency?.attributes?.exchangeRateToEGP || null;
  }
}

export const currenciesServiceExtended = new CurrencyService(API_CONFIG.ENDPOINTS.CURRENCIES);

// Invoice Service with custom methods
export class InvoiceService extends ResourceService {
  async getOverdueInvoices(): Promise<ApiResponse<any[]>> {
    const today = new Date().toISOString().split('T')[0];
    return this.getFiltered({
      status: { $eq: 'overdue' },
      dueDate: { $lt: today },
    }, {
      populate: {
        client: ['name'],
        vessel: ['name'],
        currency: ['code', 'symbol'],
      },
    });
  }

  async getInvoicesByDateRange(startDate: string, endDate: string): Promise<ApiResponse<any[]>> {
    return this.getFiltered({
      issueDate: { $gte: startDate, $lte: endDate },
    }, {
      populate: '*',
    });
  }

  async getInvoicesByClient(clientId: string | number): Promise<ApiResponse<any[]>> {
    return this.getFiltered({
      client: { id: { $eq: clientId } },
    }, {
      populate: '*',
      sort: [{ field: 'issueDate', order: 'desc' }],
    });
  }
}

export const invoicesServiceExtended = new InvoiceService(API_CONFIG.ENDPOINTS.INVOICES);

// Employee Service with custom methods
export class EmployeeService extends ResourceService {
  async getActiveEmployees(): Promise<ApiResponse<any[]>> {
    return this.getFiltered(
      { status: { $eq: 'active' } },
      {
        populate: {
          branch: ['name', 'type'],
        },
        sort: [{ field: 'name', order: 'asc' }],
      }
    );
  }

  async getEmployeesByBranch(branchId: string | number): Promise<ApiResponse<any[]>> {
    return this.getFiltered({
      branch: { id: { $eq: branchId } },
      status: { $eq: 'active' },
    }, {
      populate: '*',
    });
  }

  async getExpiringDocuments(days: number = 30): Promise<ApiResponse<any[]>> {
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + days);
    const futureDateStr = futureDate.toISOString().split('T')[0];

    return this.getFiltered({
      $or: [
        { cardExpiryDate: { $lte: futureDateStr } },
        { marinePassportExpiry: { $lte: futureDateStr } },
        { seamanBookExpiry: { $lte: futureDateStr } },
      ],
    }, {
      populate: '*',
    });
  }
}

export const employeesServiceExtended = new EmployeeService(API_CONFIG.ENDPOINTS.EMPLOYEES);

// Product Service with inventory helpers
export class ProductService extends ResourceService {
  async getLowStockProducts(): Promise<ApiResponse<any[]>> {
    // Note: This requires custom logic on backend
    // For now, fetch all and filter client-side
    const response = await this.getAll({
      populate: {
        category: ['name'],
        currency: ['code', 'symbol'],
      },
    });

    // Filter low stock on client side
    const lowStock = response.data.filter((product: any) => {
      const qty = product.attributes.quantity;
      const minLevel = product.attributes.minStockLevel;
      return qty <= minLevel;
    });

    return { ...response, data: lowStock };
  }

  async getProductsByCategory(categoryId: string | number): Promise<ApiResponse<any[]>> {
    return this.getFiltered({
      category: { id: { $eq: categoryId } },
      isActive: { $eq: true },
    }, {
      populate: '*',
    });
  }
}

export const productsServiceExtended = new ProductService(API_CONFIG.ENDPOINTS.PRODUCTS);
